'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { useAtom } from "jotai";

// lib files
import CompanionState from "@/lib/types/atoms/companionState";
import { searchResultsAtom, searchResultsStatus } from "@/lib/atoms/search-results";
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { ShowRowContent } from "@/lib/types/api/show-data";
import { StationData } from "@/lib/types/api/stations-data";
import {
  APP_TERMS,
  DONATE_TERMS,
  KIDS_TERMS,
  PASSPORT_TERMS,
} from "@/lib/content-services/search/promotedResults";

// constants
import { RECOMMENDED_SHOWS_FEATURE_CATEGORY } from "@/lib/constants";

// components
import DonateSearchRow from "@/components/Search/DonateSearchRow/DonateSearchRow";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import PassportSearchRow from "@/components/Search/PassportSearchRow/PassportSearchRow";
import PBSAppSearchRow from "@/components/Search/PBSAppSearchRow/PBSAppSearchRow";
import PBSKidsSearchRow from "@/components/Search/PBSKidsSearchRow/PBSKidsSearchRow";
import SearchFilters from "@/components/Search/SearchFilters/SearchFilters";
import SearchInput from "@/components/Search/SearchInput/SearchInput";
import SearchLandingError from "@/components/Search/SearchLandingError/SearchLandingError";
import SearchPagination from "@/components/Search/SearchPagination/SearchPagination";
import SearchRequest from "@/app/(main)/search/SearchRequest";
import SearchShowResults from "@/components/Search/SearchShowResults/SearchShowResults";
import SearchVideoAndWebResults from "@/components/Search/SearchVideoAndWebResults/SearchVideoAndWebResults";
import ShowRow from "@/components/ShowRow/ShowRow";

// styles
import styles from './SearchLanding.module.scss';

interface SearchLandingProps {
  searchParams?: { q: string };
  popularShows: ShowRowContent[];
  stationData?: StationData;
}

function SearchLanding(props: SearchLandingProps) {
  const { searchParams, popularShows, stationData } = props;
  const query = searchParams?.q;

  const annualPassportQualifyingAmount = stationData?.attributes.annual_passport_qualifying_amount;
  const truncatedStationData = {
    id: stationData?.id,
    commonName: stationData?.attributes.short_common_name,
  }

  const [searchResults] = useAtom(searchResultsAtom);
  const [resultsStatus] = useAtom(searchResultsStatus)

  const hasShowResults = searchResults?.rawResults.shows && searchResults.rawResults.shows.content.length > 0;
  const emptyOrError = [CompanionState.IsLoadedWithoutData, CompanionState.Error].includes(resultsStatus);
  const hasLoaded = searchResults !== null && resultsStatus === CompanionState.IsLoadedWithData

  const isPassportQuery = query && (PASSPORT_TERMS.includes(query.trim().toLowerCase()));
  const isPBSKidsQuery = query && (KIDS_TERMS.includes(query.trim().toLowerCase()));
  const isDonateQuery = query && (DONATE_TERMS.includes(query.trim().toLowerCase()));
  const isPBSAppQuery = query && (APP_TERMS.includes(query.trim().toLowerCase()));

  let content;

  switch (true) {
    case !query:
      content = (
        <ShowRow
          shows={popularShows}
          title='Suggested Shows'
          onShowPosterClick={(show: ShowRowContent, index?: number, linkHref?: string) => {
            const {slug, title} = show;
            sendPbsGtmEvent({
              feature_category: RECOMMENDED_SHOWS_FEATURE_CATEGORY,
              feature_name: "recommended show poster",
              object_name: "show poster",
              object_type: "link",
              object_position: (index ?? 0) + 1,
              object_location: "search page recommended show row",
              object_action: "click",
              object_action_behavior: "user navigates to show page",
              object_url: linkHref,
              pbs_content_id: slug,
              show_title: title,
            });
          }}
       />)
      break;
    case query && emptyOrError:
      content = <SearchLandingError query={query} />
      break;
    case query && hasLoaded:
      content = (
        <div className={styles.search_landing__content}>
          <SearchFilters
            className={styles.search_landing__filters}
            station={truncatedStationData}
          />
          <div className={styles.search_landing__results}>
            { isPBSKidsQuery && <PBSKidsSearchRow /> }
            { isPassportQuery && (
              <PassportSearchRow stationData={stationData} />
            )}
            { isPBSAppQuery && (
              <PBSAppSearchRow stationData={stationData}  />
            )}
            { isDonateQuery && (
              <DonateSearchRow stationData={stationData} />
            )}
            { hasShowResults && (
              <SearchShowResults
                searchShowResults={searchResults.rawResults.shows!}
              />
            )}
            <SearchVideoAndWebResults
              videoAndWebResults={searchResults.rawResults.videoAndWebResults!}
              promotedResults={searchResults.promotedResults}
              totalResults={searchResults.totalResults}
              query={query}
              station={truncatedStationData}
            />
          </div>
          <SearchPagination />
        </div>
      )
      break;
    default:
       content = <LoadingSpinner
          text="Loading Search Results..."
          className={styles.search_landing__loading}
        />
      break;
  }

  return (
    <>
      <SearchRequest stationCommonName={truncatedStationData.commonName} annualPassportQualifyingAmount={annualPassportQualifyingAmount} data-sentry-element="SearchRequest" data-sentry-source-file="SearchLanding.tsx" />
      <SearchInput query={query || ''} data-sentry-element="SearchInput" data-sentry-source-file="SearchLanding.tsx"/>
      {content}
    </>
  )
}

export default SearchLanding
